import { ref } from '@vue/composition-api'
import { format } from 'date-fns'
import * as XLSX from 'xlsx/xlsx.mjs'
import pLimit from 'p-limit'
import { generateUniqueSheetName } from '@/views/v2-temp/report/aggregation-export/composables/useExcel'
import CustomerReferralApi from '@/api/waiter/CustomerReferral'

// 顧客一覧のエクスポート処理（CSV / 顧客ごとにシートを分けた紹介売上明細Excel）
export default function useCustomerListExport(vm, { customers, appliedFilters, fetchCustomersCsv }) {
  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
    URL.revokeObjectURL(url)
  }

  // 顧客一覧CSVダウンロード（適用中フィルターの状態をそのまま反映）
  const handleDownloadCsv = async () => {
    const data = await fetchCustomersCsv(appliedFilters.value)
    if (!data) return

    downloadBlob(new Blob([data], { type: 'text/csv' }), `customers_${format(new Date(), 'yyyyMMdd')}.csv`)
  }

  // 顧客の紹介売上明細を全ページ分取得（期間指定可）
  const fetchAllReferralSalesDetails = async (customerId, { startDate, endDate }) => {
    const details = []
    let page = 1
    let totalPages = 1

    do {
      // eslint-disable-next-line no-await-in-loop
      const res = await CustomerReferralApi.getReferralSales(customerId, {
        startDate, endDate, page, perPage: 100,
      })
      details.push(...(res.data.referralSalesDetails || []))
      totalPages = res.data.pagination?.totalPages || 1
      page += 1
    } while (page <= totalPages)

    return details
  }

  // 1顧客分のシート行を構築（日付 / 紹介先顧客 / 売上 / 10% + 合計行。指名者は含めない）
  const buildReferralSalesSheetRows = details => {
    const rows = [['日付', '紹介先顧客', '売上', '10%']]

    let totalSales = 0
    let totalTenPercent = 0
    details.forEach(detail => {
      const sales = detail.salesAmount || 0
      const tenPercent = Math.floor(sales * 0.1)
      totalSales += sales
      totalTenPercent += tenPercent
      rows.push([detail.date, detail.referredCustomerName, sales, tenPercent])
    })

    rows.push(['合計', null, totalSales, totalTenPercent])

    return rows
  }

  // 顧客ごとにシートを分けた紹介売上明細Excelをダウンロード
  // 明細の対象期間は絞り込みの「集計期間」に従う（未指定なら全期間）
  const downloadingExcel = ref(false)
  const handleDownloadExcel = async () => {
    if (customers.value.length === 0) {
      vm.$toast.info('ダウンロード対象の顧客がいません')

      return
    }

    const { startDate = null, endDate = null } = appliedFilters.value

    downloadingExcel.value = true
    try {
      const limit = pLimit(5)
      const sheetsData = await Promise.all(customers.value.map(customer => limit(async () => ({
        customer,
        details: await fetchAllReferralSalesDetails(customer.id, { startDate, endDate }),
      }))))

      const workbook = XLSX.utils.book_new()
      const usedSheetNames = []

      sheetsData.forEach(({ customer, details }) => {
        const name = customer.attributes?.name || `顧客${customer.id}`
        const sheetName = generateUniqueSheetName(name, usedSheetNames)
        usedSheetNames.push(sheetName)
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(buildReferralSalesSheetRows(details)), sheetName)
      })

      XLSX.writeFile(workbook, `referral_sales_${format(new Date(), 'yyyyMMdd')}.xlsx`)
    } catch (error) {
      vm.$toast.error('Excelの作成に失敗しました')
    } finally {
      downloadingExcel.value = false
    }
  }

  return {
    handleDownloadCsv,
    downloadingExcel,
    handleDownloadExcel,
  }
}
